/**
 * Sleutel en peilwaarde per melding — pure functies, geen React, geen store.
 *
 * Meldingen in het paneel worden bij elke render VERS berekend en nergens opgeslagen
 * (analyseKnelpunten/conflictenMetDatum in Weekrooster.jsx, en ConflictBanner.jsx). Om er een
 * af te kunnen vinken is dus twee dingen nodig die de melding zelf niet heeft: een SLEUTEL die
 * dezelfde melding na een nieuwe generatie herkent, en een PEILWAARDE waarmee te zien is of de
 * situatie sindsdien is veranderd. Dit bestand levert allebei; de stand zelf woont in de store
 * (afgevinkteMeldingen).
 *
 * Zelfde opzet als lib/ruilmarkering.js en lib/handmatigmarkering.js: puur opzoeken en
 * vergelijken, niets muteren, en een adres telt alleen mee als het COMPLEET is.
 *
 * WELKE SOORTEN. Vijf: onderbezetting, gemeente, reeks, rust, dubbel. De drie knelpunt-typen
 * (vrije_medewerker, slot_tekort, balans_tekort) staan er bewust NIET in — die dragen geen
 * maand- of jaaranker (de suggesties uit analyseKnelpunten), dus dezelfde sleutel zou elke
 * maand opnieuw ontstaan en een vinkje zou naar een andere maand lekken.
 */

// Een sleutel telt alleen als ELK deel gevuld is. Geen terugval op een ontbrekend veld: een
// half adres kan op de melding van een andere week of een andere dienst landen, en een vinkje
// op de verkeerde melding is erger dan geen vinkje. Zelfde regel en zelfde reden als
// slotCompleet in ruilmarkering.js:25-31.
function samen(delen) {
  if (delen.some((d) => d === undefined || d === null || d === '')) return null;
  return delen.join('|');
}

/**
 * De sleutel van één melding, of null als het adres niet compleet is.
 *
 * Het SOORT staat vooraan, zodat twee soorten nooit op dezelfde sleutel kunnen landen — ook
 * niet als hun adresvelden toevallig gelijk zijn (rust en dubbel delen bijvoorbeeld
 * isoJaar/weeknummer/dag).
 *
 * De vijf sleutels, alle vijf uit wat de melding al draagt:
 *   onderbezetting — isoJaar, weeknummer, gemeenteKey, dienst, dag. Het SLOT, dus niet
 *                    bezet/nodig/ernst: die veranderen als er iemand bij komt, terwijl het
 *                    dezelfde melding blijft. ⚠️ isoJaar staat NIET op het conflict-object
 *                    (conflictenMetDatum in Weekrooster.jsx) en wordt daar in de renderlaag bijgezet.
 *   gemeente       — de sleutel die ConflictBanner.jsx:126 al bouwt
 *                    (isoJaar|weeknummer|dag|gemeenteKey|dienst|reden). Hergebruikt en niet
 *                    nagebouwd: twee definities van dezelfde groepering zouden vroeg of laat
 *                    uiteenlopen.
 *   rust           — mwKey, isoJaar, weeknummer, laatDag. Eén persoon, één nacht.
 *   dubbel         — medewerker (de NAAM), isoJaar, weeknummer, dag. Bewust de naam: dit
 *                    signaal draagt geen mwKey (dubbeleSignalen in analyseer-week.js). Gevolg: bij een
 *                    naamswijziging in Beheer vervalt het vinkje. Dat is te verkiezen boven
 *                    het alternatief — geen sleutel, dus geen vinkje.
 *   reeks          — mwKey, isoJaar, vanWeek, vanDag. Het BEGIN van de reeks; het einde zit
 *                    in de peilwaarde (lengte), zodat een langer of korter wordende reeks het
 *                    vinkje laat vervallen in plaats van de sleutel te veranderen.
 */
export function meldingSleutel(m) {
  if (!m || typeof m !== 'object') return null;
  switch (m.soort) {
    case 'onderbezetting':
      return samen(['onderbezetting', m.isoJaar, m.weeknummer, m.gemeenteKey, m.dienst, m.dag]);
    case 'gemeente':
      return m.sleutel ? `gemeente|${m.sleutel}` : null;
    case 'rust':
      return samen(['rust', m.mwKey, m.isoJaar, m.weeknummer, m.laatDag]);
    case 'dubbel':
      return samen(['dubbel', m.medewerker, m.isoJaar, m.weeknummer, m.dag]);
    case 'reeks':
      return samen(['reeks', m.mwKey, m.isoJaar, m.vanWeek, m.vanDag]);
    default:
      return null;
  }
}

// Peilwaarde voor een soort waar niets kan veranderen zonder dat de sleutel meeverandert.
// Eén vaste waarde houdt de vergelijking in vinkStand identiek voor alle vijf de soorten —
// geen aparte tak die "deze soort vervalt nooit" apart moet regelen.
//
// ⚠️ ALLEEN RUST valt hieronder, en dat is nagelopen: het rust-signaal draagt
// { medewerker, mwKey, laatDag, dagDag } (rustSignalen in analyseer-week.js) en `dagDag` is per definitie
// DAGEN[d+1] van `laatDag` — dus volledig bepaald door de sleutel. WELKE diensten het zijn
// staat niet eens op het signaal. Er is dus niets dat stil kan veranderen terwijl de sleutel
// gelijk blijft.
//
// Gemeente en dubbel stonden hier eerst óók, en dat was fout — zie meldingPeil hieronder.
const GEEN_GRADEN = 'x';

// Een lijst als peilwaarde: gesorteerd en samengevoegd, zodat alleen de INHOUD telt en niet
// de volgorde waarin de engine hem toevallig opleverde. Zelf sorteren en niet vertrouwen op
// de aanroeper: deze functie moet hetzelfde antwoord geven waar hij ook vandaan wordt geroepen.
function lijstPeil(waarden) {
  if (!Array.isArray(waarden) || waarden.length === 0) return null;
  return [...waarden].map(String).sort().join('|');
}

/**
 * De peilwaarde van één melding: wat er bij het afvinken wordt vastgelegd.
 *
 *   onderbezetting — `bezet` (het aantal ingeplande personen)
 *   reeks          — `lengte` (het aantal aaneengesloten dagen)
 *   gemeente       — de NAMEN, gesorteerd
 *   dubbel         — de PLAATSINGEN (gemeente/dienst), gesorteerd
 *   rust           — GEEN_GRADEN, zie hierboven
 *
 * WAAROM GEMEENTE EN DUBBEL WÉL EEN PEILWAARDE HEBBEN. Bij allebei kan de INHOUD van de
 * melding veranderen terwijl de sleutel gelijk blijft — en dan zou een vinkje een nieuwe
 * situatie stilzwijgend blijven verbergen:
 *
 *   gemeente — de namen zitten NIET in de sleutel (ConflictBanner.jsx:147 zet ze apart in
 *              `namen`, meldingSleutel kent ze niet). Staan er vier mensen fout op een
 *              cel en komt er een vijfde bij, dan blijft de sleutel gelijk en zou die vijfde
 *              mee-afgevinkt zijn. Dat is precies wat bij onderbezetting niet mocht: van 2/4
 *              naar 3/4 hoort ook terug te komen.
 *   dubbel   — `plaatsingen` zit niet in de sleutel (dubbeleSignalen in analyseer-week.js). Dezelfde persoon
 *              op dezelfde dag kan van twee naar drie diensten gaan, of van twee naar twee
 *              ANDERE diensten. De sleutel merkt daar niets van.
 *
 * `aantal` alleen zou niet volstaan: gaan er twee namen weg en komen er twee bij, dan blijft
 * het getal gelijk terwijl het andere mensen zijn. Daarom de namen/plaatsingen zelf.
 *
 * Altijd als STRING. De waarde gaat de store in en daarmee de back-up en de PUT; een string
 * vergelijkt met === zonder verrassingen bij 0, en blijft na een JSON-ronde precies hetzelfde.
 * null = niet te peilen (onbekende soort of ontbrekend gegeven) → dan kan er niet afgevinkt
 * worden, zie vinkStand.
 */
export function meldingPeil(m) {
  switch (m?.soort) {
    case 'onderbezetting':
      return Number.isFinite(m.bezet) ? String(m.bezet) : null;
    case 'reeks':
      return Number.isFinite(m.lengte) ? String(m.lengte) : null;
    case 'gemeente':
      return lijstPeil(m.namen);
    case 'dubbel':
      // Eén plaatsing is { gemKey, dienstId }; als tekst zodat de vergelijking op inhoud gaat.
      return lijstPeil((m.plaatsingen || []).map((p) => `${p?.gemKey}/${p?.dienstId}`));
    case 'rust':
      return GEEN_GRADEN;
    default:
      return null;
  }
}

/**
 * De stand van één melding tegen de afgevinkt-administratie.
 *
 * DIT IS DE ENIGE PLEK WAAR DE PEILWAARDEN WORDEN VERGELEKEN. Zowel "is hij afgevinkt" als
 * "is het vinkje vervallen" komt hieruit, zodat die twee nooit uit elkaar kunnen lopen.
 *
 * De vergelijking is `===`, dus ELKE afwijking laat het vinkje vervallen — er is bewust geen
 * richting en geen drempel. Een onderbezetting die van 2/4 naar 1/4 gaat komt terug, en van
 * 2/4 naar 3/4 net zo goed: de dienst is nog steeds niet compleet en dat hoort direct te
 * zien te zijn. Zelfde voor een reeks die van 6 naar 7 óf naar 5 dagen gaat.
 *
 * @param {object} afgevinkt  store.afgevinkteMeldingen — { [sleutel]: { peil, op } }.
 *                            Mag ontbreken (undefined) — dan is niets afgevinkt.
 * @param {object} m          de melding, met `soort` en zijn adresvelden.
 * @returns {{sleutel: string|null, stand: 'geen'|'afgevinkt'|'vervallen', peilToen: string|null}}
 *   stand 'geen'      = niet afgevinkt (of niet afvinkbaar: geen sleutel of geen peilwaarde)
 *   stand 'afgevinkt' = afgevinkt én de situatie is ongewijzigd → uit de lijst
 *   stand 'vervallen' = ooit afgevinkt, maar de peilwaarde is veranderd → weer in de lijst
 */
export function vinkStand(afgevinkt, m) {
  const sleutel = meldingSleutel(m);
  if (!sleutel) return { sleutel: null, stand: 'geen', peilToen: null };

  const vink = afgevinkt?.[sleutel];
  if (!vink) return { sleutel, stand: 'geen', peilToen: null };

  const nu = meldingPeil(m);
  // Niet te peilen → behandelen als niet afgevinkt. Liever een melding te veel tonen dan er
  // een verbergen op grond van een vergelijking die niet gemaakt kon worden.
  if (nu === null) return { sleutel, stand: 'geen', peilToen: vink.peil ?? null };

  const gelijk = vink.peil === nu;
  return { sleutel, stand: gelijk ? 'afgevinkt' : 'vervallen', peilToen: vink.peil ?? null };
}

/**
 * Hoeveel MELDINGEN dekt deze regel? Eén gemeente-regel kan er meer dan één dekken, omdat
 * gelijke meldingen worden samengevoegd (groepeerGemeenteSignalen in ConflictBanner.jsx). De
 * tellers daar tellen meldingen en geen regels (telMeldingen), dus afvinken haalt er `aantal`
 * uit en niet 1.
 */
export function meldingAantal(m) {
  return Number.isFinite(m?.aantal) ? m.aantal : 1;
}
